import { z } from 'zod';

/**
 * Schemas Zod du module Interventions (Phase 11 — US-INT-01 à 10 + US-SVC-08).
 *
 * Couvre :
 *  - CRUD demande d'intervention (US-INT-02) + filtres avancés (US-INT-10)
 *  - Workflow statuts (US-INT-03)
 *  - Validation multi-niveaux (US-INT-04)
 *  - Affectation tech/équipe/prestataire (US-INT-05)
 *  - Saisie terrain : pièces (US-INT-06)
 *  - Clôture & évaluation satisfaction (US-INT-07)
 *  - Urgences P0/P1 (US-INT-08)
 *  - Vue d'ensemble (US-INT-01)
 *  - Prestataires (sous-module)
 *  - KPIs services (US-SVC-08)
 */

// ─── Constantes ─────────────────────────────────────────────────────────────

export const INTERVENTION_TYPES_DEMANDE = [
  'panne',
  'urgence_critique',
  'preventive',
  'amelioration',
  'installation',
  'demenagement',
  'controle_reglementaire',
  'expertise',
] as const;
export type InterventionTypeDemande = (typeof INTERVENTION_TYPES_DEMANDE)[number];

export const INTERVENTION_PRIORITES = ['P0', 'P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'] as const;
export type InterventionPriorite = (typeof INTERVENTION_PRIORITES)[number];

export const INTERVENTION_STATUTS = [
  'nouveau',
  'en_attente_validation',
  'affecte',
  'planifie',
  'en_cours',
  'en_attente_pieces',
  'en_attente_validation_finale',
  'cloture',
  'annule',
] as const;
export type InterventionStatut = (typeof INTERVENTION_STATUTS)[number];

export const INTERVENTION_MODE_AFFECTATION = [
  'automatique',
  'manuelle',
  'auto_affectation',
  'equipe',
  'externe',
] as const;
export type InterventionModeAffectation = (typeof INTERVENTION_MODE_AFFECTATION)[number];

export const INTERVENTION_ETATS_FINAUX = ['fonctionnel', 'degrade', 'hors_service'] as const;
export type InterventionEtatFinal = (typeof INTERVENTION_ETATS_FINAUX)[number];

export const INTERVENTION_TYPES_VALIDATION = [
  'technique',
  'budgetaire',
  'service',
  'securite',
  'qualite',
  'direction',
] as const;
export type InterventionTypeValidation = (typeof INTERVENTION_TYPES_VALIDATION)[number];

export const INTERVENTION_STATUTS_VALIDATION = ['en_attente', 'approuve', 'rejete'] as const;
export type InterventionStatutValidation = (typeof INTERVENTION_STATUTS_VALIDATION)[number];

export const INTERVENTION_HISTORIQUE_ACTIONS = [
  'creation',
  'changement_statut',
  'affectation',
  'validation',
  'rejet_validation',
  'planification',
  'demarrage',
  'pause',
  'reprise',
  'pieces_ajoutees',
  'cloture',
  'evaluation',
  'annulation',
] as const;
export type InterventionHistoriqueAction = (typeof INTERVENTION_HISTORIQUE_ACTIONS)[number];

// ─── Demande d'intervention CRUD (US-INT-02 / US-INT-10) ────────────────────

export const createDemandeInterventionSchema = z.object({
  titre: z.string().min(1).max(255),
  description: z.string().min(1),
  type_demande: z.enum(INTERVENTION_TYPES_DEMANDE),
  priorite: z.enum(INTERVENTION_PRIORITES).default('P4'),
  service_demandeur_id: z.string().uuid().optional().nullable(),
  equipement_id: z.string().uuid().optional().nullable(),
  date_planifiee: z.string().datetime().optional().nullable(),
  /** minutes */
  duree_estimee: z.coerce.number().int().min(1).optional().nullable(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateDemandeInterventionInput = z.infer<typeof createDemandeInterventionSchema>;

/**
 * PATCH d'une demande — interdit de toucher au statut, au numéro et aux dates
 * d'exécution (qui ont leurs routes dédiées). Le statut est piloté par la
 * machine à états (POST /:id/affecter, /planifier, /demarrer, …).
 */
export const updateDemandeInterventionSchema = z.object({
  titre: z.string().min(1).max(255).optional(),
  description: z.string().min(1).optional(),
  type_demande: z.enum(INTERVENTION_TYPES_DEMANDE).optional(),
  priorite: z.enum(INTERVENTION_PRIORITES).optional(),
  service_demandeur_id: z.string().uuid().nullable().optional(),
  equipement_id: z.string().uuid().nullable().optional(),
  date_planifiee: z.string().datetime().nullable().optional(),
  duree_estimee: z.coerce.number().int().min(1).nullable().optional(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type UpdateDemandeInterventionInput = z.infer<typeof updateDemandeInterventionSchema>;

/**
 * Filtres / pagination de la liste (US-INT-10).
 * Le filtre `statut` peut être un seul statut OU une liste CSV pour supporter
 * les onglets (US-INT-09) — ex. `?statut=nouveau,en_attente_validation`.
 */
export const listInterventionsQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  /** Accepte un statut unique ou une liste CSV (`nouveau,affecte`). */
  statut: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) return undefined;
      const parts = val
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return parts.length > 0 ? parts : undefined;
    })
    .pipe(z.array(z.enum(INTERVENTION_STATUTS)).optional()),
  priorite: z.enum(INTERVENTION_PRIORITES).optional(),
  type_demande: z.enum(INTERVENTION_TYPES_DEMANDE).optional(),
  service_demandeur_id: z.string().uuid().optional(),
  equipement_id: z.string().uuid().optional(),
  technicien_affecte_id: z.string().uuid().optional(),
  demandeur_id: z.string().uuid().optional(),
  prestataire_id: z.string().uuid().optional(),
  date_demande_from: z.string().datetime().optional(),
  date_demande_to: z.string().datetime().optional(),
  sort_by: z
    .enum(['date_demande', 'numero_demande', 'priorite', 'statut', 'titre', 'created_at'])
    .default('date_demande'),
  sort_order: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListInterventionsQuery = z.infer<typeof listInterventionsQuerySchema>;

// ─── Workflow statuts (US-INT-03 / US-INT-05) ───────────────────────────────

/**
 * Affectation : soit un technicien (ou null pour désaffecter), soit une équipe,
 * soit un prestataire externe. Au moins un des trois doit être fourni — la
 * validation finale est faite côté service.
 */
export const affecterTechnicienSchema = z.object({
  technicien_affecte_id: z.string().uuid().nullable().optional(),
  /** Liste d'IDs utilisateurs membres d'une équipe — pour mode='equipe'. */
  equipe_affectee: z.array(z.string().uuid()).optional(),
  prestataire_id: z.string().uuid().nullable().optional(),
  mode_affectation: z.enum(INTERVENTION_MODE_AFFECTATION),
  date_planifiee: z.string().datetime().optional().nullable(),
  duree_estimee: z.coerce.number().int().min(1).optional().nullable(),
});
export type AffecterTechnicienInput = z.infer<typeof affecterTechnicienSchema>;

export const planifierSchema = z.object({
  date_planifiee: z.string().datetime(),
  duree_estimee: z.coerce.number().int().min(1).optional().nullable(),
});
export type PlanifierInput = z.infer<typeof planifierSchema>;

export const demarrerInterventionSchema = z.object({
  diagnostic_initial: z.string().max(4000).optional().nullable(),
});
export type DemarrerInterventionInput = z.infer<typeof demarrerInterventionSchema>;

export const pauseSchema = z.object({
  motif: z.string().min(1).max(1000),
  /** En MVP seul `en_attente_pieces` est supporté. */
  nouveau_statut: z.literal('en_attente_pieces').default('en_attente_pieces'),
});
export type PauseInput = z.infer<typeof pauseSchema>;

export const cloturerSchema = z.object({
  diagnostic_final: z.string().min(1),
  cause_panne: z.string().max(2000).optional().nullable(),
  actions_realisees: z.string().min(1),
  etat_final: z.enum(INTERVENTION_ETATS_FINAUX),
  cout_main_oeuvre: z.coerce.number().min(0).optional().nullable(),
  cout_pieces: z.coerce.number().min(0).optional().nullable(),
  cout_deplacement: z.coerce.number().min(0).optional().nullable(),
  date_fin_intervention: z.string().datetime().optional().nullable(),
});
export type CloturerInput = z.infer<typeof cloturerSchema>;

export const annulerInterventionSchema = z.object({
  motif: z.string().min(1).max(1000),
});
export type AnnulerInterventionInput = z.infer<typeof annulerInterventionSchema>;

// ─── Évaluation satisfaction (US-INT-07) ────────────────────────────────────

export const evaluerSchema = z.object({
  note_satisfaction: z.coerce.number().int().min(1).max(5),
  evaluation_delai: z.coerce.number().int().min(1).max(5).optional().nullable(),
  evaluation_qualite: z.coerce.number().int().min(1).max(5).optional().nullable(),
  evaluation_comportement: z.coerce.number().int().min(1).max(5).optional().nullable(),
  commentaire_satisfaction: z.string().max(4000).optional().nullable(),
});
export type EvaluerInput = z.infer<typeof evaluerSchema>;

// ─── Validations multi-niveaux (US-INT-04) ──────────────────────────────────

export const createValidationSchema = z.object({
  type_validation: z.enum(INTERVENTION_TYPES_VALIDATION),
  valideur_id: z.string().uuid(),
});
export type CreateValidationInput = z.infer<typeof createValidationSchema>;

export const repondreValidationSchema = z.object({
  approve: z.boolean(),
  commentaire: z.string().max(4000).optional().nullable(),
});
export type RepondreValidationInput = z.infer<typeof repondreValidationSchema>;

// ─── Pièces utilisées (US-INT-06) ───────────────────────────────────────────

export const createPieceSchema = z.object({
  designation: z.string().min(1).max(255),
  reference: z.string().max(100).optional().nullable(),
  quantite: z.coerce.number().positive().default(1),
  cout_unitaire: z.coerce.number().min(0).optional().nullable(),
  date_utilisation: z.string().date().optional().nullable(),
});
export type CreatePieceInput = z.infer<typeof createPieceSchema>;

export const updatePieceSchema = z.object({
  designation: z.string().min(1).max(255).optional(),
  reference: z.string().max(100).nullable().optional(),
  quantite: z.coerce.number().positive().optional(),
  cout_unitaire: z.coerce.number().min(0).nullable().optional(),
  date_utilisation: z.string().date().nullable().optional(),
});
export type UpdatePieceInput = z.infer<typeof updatePieceSchema>;

// ─── Prestataires ───────────────────────────────────────────────────────────

const prestataireContactSchema = z
  .object({
    email: z.string().email().or(z.literal('')).optional().nullable(),
    telephone: z.string().max(50).optional().nullable(),
    adresse: z.string().max(500).optional().nullable(),
  })
  .partial();

export const createPrestataireSchema = z.object({
  nom: z.string().min(1).max(255),
  specialite: z.string().max(100).optional().nullable(),
  contact: prestataireContactSchema.optional().nullable(),
  is_active: z.boolean().default(true),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type CreatePrestataireInput = z.infer<typeof createPrestataireSchema>;

export const updatePrestataireSchema = createPrestataireSchema.partial();
export type UpdatePrestataireInput = z.infer<typeof updatePrestataireSchema>;

export const listPrestatairesQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  is_active: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListPrestatairesQuery = z.infer<typeof listPrestatairesQuerySchema>;

// ─── KPIs services périodiques (US-SVC-08) ──────────────────────────────────

/** YYYY-MM (ex. '2026-04'). */
const periodeMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Format attendu : YYYY-MM');

export const recomputeServiceKpisQuerySchema = z.object({
  periode: periodeMonthSchema.optional(),
});
export type RecomputeServiceKpisQuery = z.infer<typeof recomputeServiceKpisQuerySchema>;

export const listServiceKpisQuerySchema = z
  .object({
    from: periodeMonthSchema.optional(),
    to: periodeMonthSchema.optional(),
  })
  .refine(
    (v) => !v.from || !v.to || v.from <= v.to,
    'Plage incohérente : "from" doit être <= "to"',
  );
export type ListServiceKpisQuery = z.infer<typeof listServiceKpisQuerySchema>;
